'use client';

import { useCallback, useEffect, useRef } from 'react';
import {
  useTerminalStore,
  useTerminalTabs,
  type ShellType,
} from '../store/terminalStore';
import { getCurrentTenantId } from '../store/tenantStorage';
import {
  ensureInitialized,
  ensureSessionTerminal,
  getSessionMapping,
  killSessionTerminal,
  type EnsureTerminalResult,
  type SessionMapping,
} from './ensure-session-terminal';

export type { EnsureTerminalResult, SessionMapping };

export { ensureSessionTerminal, killSessionTerminal };

type TerminalSource = 'ai' | 'user';

export function useTerminalExecution() {
  const {
    getOrCreateTerminalTab,
    refreshTerminalSession,
    defaultShell,
    setDefaultShell,
  } = useTerminalTabs();
  const hasLoadedDefaultShellPreferenceRef = useRef(false);
  const isElectron =
    typeof window !== 'undefined' && !!window.electronAPI?.terminal;
  const scopeKey = getCurrentTenantId() || 'global';

  useEffect(() => {
    if (!isElectron) return;
    hasLoadedDefaultShellPreferenceRef.current = false;

    const initialize = async () => {
      try {
        await ensureInitialized();
      } catch (error) {
        console.warn(
          '[TerminalExecution] Failed to initialize sessions',
          error,
        );
      }

      try {
        const shell =
          await window.electronAPI.terminal.getDefaultShell(scopeKey);

        setDefaultShell(shell);
      } catch (error) {
        console.warn(
          '[TerminalExecution] Failed to load default shell preference',
          error,
        );
      } finally {
        hasLoadedDefaultShellPreferenceRef.current = true;
      }
    };

    initialize();
  }, [isElectron, setDefaultShell, scopeKey]);

  useEffect(() => {
    if (!isElectron) return;
    if (!hasLoadedDefaultShellPreferenceRef.current) return;

    const persistShell = async () => {
      try {
        await window.electronAPI.terminal.setDefaultShell(
          defaultShell,
          scopeKey,
        );
      } catch (error) {
        console.warn(
          '[TerminalExecution] Failed to persist default shell preference',
          error,
        );
      }
    };

    persistShell();
  }, [defaultShell, isElectron, scopeKey]);

  const ensureTerminalSession = useCallback(
    async ({
      sessionId,
      cwd,
      shell,
      source,
      chatId,
      toolCallId,
      isBackground,
    }: {
      sessionId: string;
      cwd?: string;
      shell?: ShellType;
      source?: TerminalSource;
      chatId?: string;
      toolCallId?: string;
      isBackground?: boolean;
    }): Promise<EnsureTerminalResult> => {
      const hadTerminalTab = useTerminalStore
        .getState()
        .terminalTabs.some((tab) => tab.sessionId === sessionId);
      const terminalSession = await ensureSessionTerminal({
        sessionId,
        cwd,
        shell,
        scopeKey,
        defaultShell,
        source,
        chatId,
        toolCallId,
        isBackground,
      });

      const terminalShell = terminalSession.shell;

      getOrCreateTerminalTab(sessionId, {
        title: terminalShell,
        cwd,
        shell: terminalShell,
      });

      if (hadTerminalTab && terminalSession.isNewSession) {
        refreshTerminalSession(sessionId, {
          title: terminalShell,
          cwd,
          shell: terminalShell,
        });
      }

      return terminalSession;
    },
    [defaultShell, getOrCreateTerminalTab, refreshTerminalSession, scopeKey],
  );

  const getTerminalId = useCallback((sessionId: string): string | undefined => {
    return getSessionMapping(sessionId)?.terminalId;
  }, []);

  const getTerminalByChatId = useCallback(
    (chatId: string): SessionMapping | undefined => {
      return getSessionMapping(chatId);
    },
    [],
  );

  const clearTerminal = useCallback(
    async (sessionId: string) => {
      const mapping = getSessionMapping(sessionId);

      if (mapping && isElectron) {
        try {
          await window.electronAPI.terminal.clear(mapping.terminalId);
        } catch (error) {
          console.warn('[TerminalExecution] Failed to clear terminal', error);
        }
      }
    },
    [isElectron],
  );

  const killTerminal = useCallback(
    (sessionId: string) => killSessionTerminal(sessionId),
    [],
  );

  return {
    ensureTerminalSession,
    getTerminalId,
    getTerminalByChatId,
    clearTerminal,
    killTerminal,
    isElectronAvailable: isElectron,
  };
}
